/**
 * Feature Flag Row — compact, selectable list item (master pane).
 */

import { AlertTriangle, Filter } from 'lucide-react';
import { useI18n } from '@/i18n';
import type { FlagResponse } from '@/types/featureFlags';
import { getExpiryInfo, isFlagStale } from '../utils/stale';
import { getFlagDisplayState, FLAG_STATE_DOT, FLAG_STATE_PILL } from '../utils/flagState';

interface FlagRowProps {
  flag: FlagResponse;
  selected: boolean;
  onSelect: (flagKey: string) => void;
}

const FLAG_TYPE_BADGE: Record<string, string> = {
  deployment: 'bg-blue-100 text-blue-700',
  release: 'bg-purple-100 text-purple-700',
  isolation: 'bg-orange-100 text-orange-700',
  ops: 'bg-teal-100 text-teal-700',
};

export function FlagRow({ flag, selected, onSelect }: FlagRowProps) {
  const { t } = useI18n();
  const stale = isFlagStale(flag);
  const { status } = getExpiryInfo(flag.expires_at);
  const isArchived = flag.lifecycle_state === 'archived';
  const { state, rollout, hasRules } = getFlagDisplayState(flag);
  const stateLabel =
    state === 'partial' && rollout > 0 && rollout < 100
      ? t('featureFlags.enabledState.rollout', { pct: String(rollout) })
      : t(`featureFlags.enabledState.${state}`);

  return (
    <button
      type="button"
      onClick={() => onSelect(flag.flag_key)}
      className={`
        w-full text-left px-4 py-3 border-l-2 transition-colors
        ${selected ? 'bg-primary-50 border-primary-500' : 'border-transparent hover:bg-gray-50'}
        ${isArchived ? 'opacity-60' : ''}
      `}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <span
          className={`w-2 h-2 rounded-full flex-shrink-0 ${FLAG_STATE_DOT[state]}`}
          title={stateLabel}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-sm font-medium text-gray-900 truncate ${flag.display_name ? '' : 'font-mono'}`}
            >
              {flag.display_name || flag.flag_key}
            </span>
            <span
              className={`px-1.5 py-0.5 text-[11px] rounded ${FLAG_TYPE_BADGE[flag.flag_type] ?? 'bg-gray-100 text-gray-600'}`}
            >
              {flag.flag_type}
            </span>
            {stale && (
              <AlertTriangle
                className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0"
                aria-label={
                  status === 'expired'
                    ? t('featureFlags.flagRow.expired')
                    : t('featureFlags.flagRow.expiringSoon')
                }
              />
            )}
          </div>
          {flag.display_name && (
            <p className="font-mono text-xs text-gray-400 mt-0.5 truncate">{flag.flag_key}</p>
          )}
          {flag.owner && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">{flag.owner}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {hasRules && (
            <Filter
              className="w-3.5 h-3.5 text-gray-400"
              aria-label={t('featureFlags.enabledState.hasRules')}
            />
          )}
          <span
            className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${FLAG_STATE_PILL[state]}`}
          >
            {stateLabel}
          </span>
        </div>
      </div>
    </button>
  );
}
